import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
} from "../../../components/ui";
import {
  fetchCrmRoles,
  createCrmRole,
  updateCrmRole,
} from "../../../services/crmSettings";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export default function CrmRolesSettings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    code: "",
    label: "",
    description: "",
    sort_order: 100,
    is_active: true,
  });

  const [editingId, setEditingId] = useState(null);

  const [authReady, setAuthReady] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      setAuthReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    load();
  }, [authReady, isLoggedIn]);

  async function load() {
    try {
      setLoading(true);
      setError("");
      const rows = await fetchCrmRoles();
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err.message || "Failed to load CRM roles");
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setForm({
      code: "",
      label: "",
      description: "",
      sort_order: 100,
      is_active: true,
    });
    setEditingId(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!form.code.trim() || !form.label.trim()) {
      setError("Code and label are required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        code: form.code.trim(),
        label: form.label.trim(),
        description: form.description.trim() || null,
        sort_order: Number(form.sort_order || 100),
        is_active: form.is_active ? 1 : 0,
      };

      if (editingId) {
        await updateCrmRole(editingId, payload);
      } else {
        await createCrmRole(payload);
      }

      await load();
      resetForm();
    } catch (err) {
      setError(err.message || "Failed to save CRM role");
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item) {
    setEditingId(item.id);
    setForm({
      code: item.code || "",
      label: item.label || "",
      description: item.description || "",
      sort_order: item.sort_order ?? 100,
      is_active: !!item.is_active,
    });
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
      <Card className="xl:col-span-2">
        <CardHeader>
          <CardTitle>CRM Roles</CardTitle>
          <CardDescription>
            Manage business roles used in workflows, activities and notifications.
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : (
            <div className="space-y-2">
              {items.map((r) => (
                <div
                  key={r.id}
                  className="border rounded-lg px-4 py-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium">
                      {r.label}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({r.code})
                      </span>
                    </div>

                    <div className="text-sm text-muted-foreground mt-1">
                      {r.description || "—"}
                    </div>

                    <div className="text-xs text-muted-foreground mt-2">
                      Sort: {r.sort_order ?? "—"} •{" "}
                      {Number(r.is_active) === 1 ? "Active" : "Inactive"}
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => startEdit(r)}>
                      Edit
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          await updateCrmRole(r.id, {
                            code: r.code,
                            label: r.label,
                            description: r.description,
                            sort_order: r.sort_order ?? 100,
                            is_active: Number(r.is_active) === 1 ? 0 : 1,
                          });
                          await load();
                        } catch (err) {
                          setError(err.message || "Failed to update CRM role");
                        }
                      }}
                    >
                      {Number(r.is_active) === 1 ? "Deactivate" : "Activate"}
                    </Button>
                  </div>
                </div>
              ))}

              {items.length === 0 && (
                <div className="text-sm text-muted-foreground">No CRM roles found.</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{editingId ? "Edit CRM role" : "New CRM role"}</CardTitle>
          <CardDescription>Create or update a CRM business role.</CardDescription>
        </CardHeader>

        <CardContent>
          <form className="space-y-3" onSubmit={handleSubmit}>
            <div>
              <div className="text-sm mb-1">Code</div>
              <Input
                value={form.code}
                onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
                placeholder="designer"
              />
            </div>

            <div>
              <div className="text-sm mb-1">Label</div>
              <Input
                value={form.label}
                onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
                placeholder="Designer"
              />
            </div>

            <div>
              <div className="text-sm mb-1">Description</div>
              <textarea
                rows={4}
                className="w-full rounded-md border px-3 py-2 bg-white dark:bg-neutral-900"
                value={form.description}
                onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                placeholder="Short description..."
              />
            </div>

            <div>
              <div className="text-sm mb-1">Sort order</div>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm((s) => ({ ...s, sort_order: e.target.value }))}
              />
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm((s) => ({ ...s, is_active: e.target.checked }))}
              />
              Active
            </label>

            <div className="flex justify-end gap-2 pt-2">
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Save changes" : "Create CRM role"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}